import { t } from '@lingui/core/macro';
import { Board, Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import supabase from 'modules/core/supabase/supabase';
import { TokenStorage } from 'modules/core/token-storage';
import { getCurrentBoard } from 'modules/overlay-v2/domain/utils/get-current-board';
import { isMatchPeriodsSettingsMissing } from 'modules/overlay-v2/domain/utils/is-match-periods-settings-missing';
import { toErrorMessage } from 'modules/overlay-v2/domain/utils/to-error-message';
import { syncGameInfo } from 'modules/overlay-v2/store/game-info/actions';
import { MISSING_PERIODS_SETTINGS_TOAST } from 'modules/overlay-v2/ui/constants/missing-periods-settings-toast';
import { setPassedEventsId as setPassedEventsIdGameInfo } from 'modules/overlay-v2/store/game-info/game-info';
import {
  setBoardLocked,
  setHideControlPanel,
  setIsMatchDataReady,
  setPassedEventsId as setPassedEventsIdOverlay,
} from 'modules/overlay-v2/store/overlay/overlay';
import { selectBroadCastState } from 'modules/overlay-v2/store/overlay/selectors';
import { preloadGameInfoAssets } from 'modules/overlay-v2/store/readiness/actions';
import {
  readinessTaskFailed,
  readinessTaskStarted,
  readinessTaskSucceeded,
  ReadinessTaskId,
  resetReadiness,
} from 'modules/overlay-v2/store/readiness/readiness';
import { selectRetryToken } from 'modules/overlay-v2/store/readiness/selectors';
import { syncTime } from 'modules/overlay-v2/store/time/actions';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import { fetchTemplateWithBoards } from 'modules/template-editor/store/templates/actions';
import { setActiveBoard, setActiveTemplate } from 'modules/template-editor/store/templates/templates';
import useOverlayRouteParams from 'modules/overlay-v2/ui/hooks/use-overlay-route-params';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { i18n } from 'shared/i18n';
import { useQueryParam } from 'use-query-params';

/** Шаг, на котором сейчас находится подготовка: нужен, чтобы `catch` знал, что пометить красным. */
type Stage = ReadinessTaskId | 'matchData';

function useInitDataOverlayV2() {
  const dispatch = useAppDispatch();

  // Битый адрес не роняет страницу: её объясняет `useOverlayRouteParams`,
  // а инициализация просто ничего не делает.
  const { organizerId: tournamentId, token } = useOverlayRouteParams();
  if (tournamentId !== undefined) supabase.overlayLogs.init({ tournamentId });

  useEffect(() => {
    if (!token) return;
    const tokenStorage = new TokenStorage();
    tokenStorage.saveToken(token);
  }, [token]);

  const boardId = useQueryParam('boardId')[0] as string | undefined;
  const isLocked = useQueryParam('locked')[0] as string | undefined;
  const isHidePanel = useQueryParam('hidePanel')[0] as string | undefined;

  useEffect(() => {
    dispatch(setActiveBoard({ id: boardId ? parseInt(boardId) : undefined }));
    dispatch(setBoardLocked(isLocked === 'true'));
    dispatch(setHideControlPanel(isHidePanel === 'true'));
  }, [boardId, dispatch, isHidePanel, isLocked]);

  const { matchId } = useAppSelector(selectBroadCastState);
  const retryToken = useAppSelector(selectRetryToken);

  useEffect(() => {
    if (!matchId) return;

    dispatch(setIsMatchDataReady(false));
    dispatch(setPassedEventsIdGameInfo([]));
    dispatch(setPassedEventsIdOverlay([]));

    supabase.overlayLogs.init({ matchId });

    let cancelled = false;
    let stage: Stage = 'matchData';

    (async () => {
      try {
        const { gameInfo } = await dispatch(syncGameInfo({ matchId })).unwrap();
        if (cancelled) return;

        stage = 'chronology';
        dispatch(readinessTaskStarted(stage));
        // Оверлей журнал матча только читает: заводит его мобильное приложение. Пока строки нет,
        // считаем журнал пустым и идём дальше — события приедут по realtime, как только
        // приложение начнёт писать. Раньше здесь стояло создание строки, и на дублях каждое
        // чтение добавляло ещё одну (см. `MatchChronology.getRow`).
        const data = await supabase.matchChronology.getRow(matchId);
        if (cancelled) return;
        const passedEvents = data?.chronology?.map((event) => event.id) ?? [];
        dispatch(setPassedEventsIdGameInfo(passedEvents));
        dispatch(setPassedEventsIdOverlay(passedEvents));
        dispatch(readinessTaskSucceeded(stage));

        stage = 'matchTime';
        dispatch(readinessTaskStarted(stage));
        await dispatch(syncTime({ matchId })).unwrap();
        if (cancelled) return;
        dispatch(readinessTaskSucceeded(stage));

        // Лого и фото игроков качаем до показа табло: Fabric должен взять их из кэша,
        // иначе доска секунду стоит с пустыми плейсхолдерами прямо в эфире.
        stage = 'assets';
        await dispatch(preloadGameInfoAssets({ gameInfo })).unwrap();
        if (cancelled) return;

        dispatch(setIsMatchDataReady(true));
      } catch (error) {
        if (cancelled) return;

        const message = toErrorMessage(error);

        // Незаполненный регламент — не сбой связи, а настройка, которую оператор может
        // поправить сам, поэтому объясняем её отдельно от общего экрана готовности.
        if (isMatchPeriodsSettingsMissing(error)) {
          toast.error(i18n._(MISSING_PERIODS_SETTINGS_TOAST.message), MISSING_PERIODS_SETTINGS_TOAST.options);
        }

        // Ошибка `matchData` уже лежит в слайсе `gameInfo`, остальные шаги отмечаем сами.
        // Раньше здесь стоял `setIsMatchDataReady(true)`: сбой проглатывался, и табло
        // выходило в эфир с нулевым счётом и пустыми составами. Теперь шаг остаётся
        // красным, а повтор назначает `useOverlayReadiness` — он видит все шаги сразу.
        if (stage !== 'matchData') dispatch(readinessTaskFailed({ id: stage, error: message }));

        supabase.overlayLogs.createError({
          comment: 'Не удалось сделать первоначальную настройку',
          message: error instanceof Error ? JSON.stringify({ message: error.message, stack: error.stack }) : message,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, matchId, retryToken]);

  // Сменился матч — чеклист начинается заново.
  //
  // Считается только переход на другой матч, а не любое изменение `matchId`. Пропавшая
  // строка `broadcast` роняет его в `undefined` — матч сняли с устройства, — и безусловный
  // сброс снимал вместе со всеми шагами латч `hasGoneLive`, возвращая полноэкранный чеклист
  // поверх идущего эфира. Это тот самый экран, которого `hasGoneLive` и должен не допускать.
  const preparedMatchId = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!matchId || matchId === preparedMatchId.current) return;

    preparedMatchId.current = matchId;
    dispatch(resetReadiness());
  }, [dispatch, matchId]);

  // Оформление грузится отдельно от данных матча, но повтор обязан задевать и его:
  // без `retryToken` в зависимостях сбой загрузки шаблона оставался навсегда, а шаг
  // чеклиста крутился вечно, потому что `isLoaded` так и не наступал.
  const { championship } = useGameInfo();
  useEffect(() => {
    if (!championship?.overlayId) return;

    dispatch(readinessTaskStarted('template'));
    dispatch(fetchTemplateWithBoards({ templateId: championship.overlayId }))
      .unwrap()
      .then((payload) => {
        const { template } = payload as { template: Template; boards: Board[] };
        dispatch(setActiveTemplate({ id: template.id }));
        dispatch(readinessTaskSucceeded('template'));
        window?.opener?.postMessage(JSON.stringify({ type: 'popupLoaded' }), '*');
      })
      .catch((data) => {
        toast.error(t`Не удалось загрузить шаблон и табло`);
        dispatch(readinessTaskFailed({ id: 'template', error: toErrorMessage(data) }));
        supabase.overlayLogs.createError({
          comment: 'Не удалось получить шаблоны пользователя',
          message: toErrorMessage(data),
        });
      });

    supabase.overlayLogs.init({
      championshipId: championship.id,
      templateId: championship.overlayId,
    });
  }, [championship?.id, championship?.overlayId, dispatch, retryToken]);

  // Первичная установка табло из журнала: запрос выполняется один раз на матч.
  //
  // Раньше эффект перезапускался на каждое изменение `isLoaded` (то есть на любую
  // перезагрузку оформления), а результат применялся безусловно. Два следствия, оба
  // наблюдались: запрос-«догоняла» перебивал свежее значение из эфира устаревшим, а
  // сорванный запрос гасил табло — `getRow` возвращает `null` на любой сетевой ошибке
  // (`match-chronology.ts:84-95`), и `null` был неотличим от честного «табло выключено».
  // Теперь гасит только успешно прочитанная строка, а отказ оставляет табло как есть.
  const { isLoaded } = useTemplates();
  const currentBoardRequestedFor = useRef<number>();
  useEffect(() => {
    if (!matchId) return;
    if (!isLoaded) return;
    if (boardId) return;
    if (currentBoardRequestedFor.current === matchId) return;

    currentBoardRequestedFor.current = matchId;
    getCurrentBoard(matchId)
      .then((currentBoard) => {
        dispatch(setActiveBoard({ id: currentBoard }));
      })
      .catch((error) => {
        console.error({ event: 'failed to read current board', error });
        supabase.overlayLogs.createError({
          comment: 'Не удалось получить активное табло',
          message: toErrorMessage(error),
        });
      });
  }, [dispatch, matchId, isLoaded, boardId]);
}

export default useInitDataOverlayV2;
